/**
 * Category configuration for Platform Settings
 * Defines icons, display names, and descriptions for each settings category
 */
import type { ComponentType, HTMLAttributes } from "react";
import {
    Clock,
    CpuChip01,
    Database01,
    File06,
    Truck01,
    Mail01,
    Inbox01,
    Lock01,
    Code01,
    UploadCloud01,
    MessageSquare01,
    CheckCircle,
    Activity,
} from "@untitledui/icons";
import type { SettingCategory } from "@/types/api/settings";

export interface CategoryConfig {
    icon: ComponentType<HTMLAttributes<HTMLOrSVGElement>>;
    displayName: string;
    description: string;
}

export const CATEGORY_CONFIG: Record<SettingCategory, CategoryConfig> = {
    scheduler: {
        icon: Clock,
        displayName: "Scheduler",
        description: "Background jobs timing and execution",
    },
    ai: {
        icon: CpuChip01,
        displayName: "AI Configuration",
        description: "LLM models, temperature, and token limits",
    },
    rag: {
        icon: Database01,
        displayName: "RAG / Knowledge Base",
        description: "Semantic search and document retrieval settings",
    },
    extraction: {
        icon: File06,
        displayName: "Document Extraction",
        description: "OCR and document processing configuration",
    },
    tracking: {
        icon: Truck01,
        displayName: "Container Tracking",
        description: "Carrier API timeouts and caching",
    },
    email: {
        icon: Mail01,
        displayName: "Email Integration",
        description: "Email sync and notification settings",
    },
    auth: {
        icon: Lock01,
        displayName: "Authentication",
        description: "Token expiration and security settings",
    },
    api: {
        icon: Code01,
        displayName: "API Configuration",
        description: "Pagination and rate limiting",
    },
    storage: {
        icon: UploadCloud01,
        displayName: "Cloud Storage",
        description: "Google Cloud Storage configuration",
    },
    eve_care_chat: {
        icon: MessageSquare01,
        displayName: "Eve Care Chat",
        description: "Chat features and attachment settings",
    },
    cross_check: {
        icon: CheckCircle,
        displayName: "Cross-Check Validation",
        description: "Document cross-check and validation rules",
    },
    activities: {
        icon: Activity,
        displayName: "Activities",
        description: "Activity logging and tracking configuration",
    },
    mail_companion: {
        icon: Inbox01,
        displayName: "Mail Companion",
        description: "Email-only container processing and domain mapping",
    },
};

/**
 * Order of categories in the UI
 */
export const CATEGORY_ORDER: SettingCategory[] = [
    "scheduler",
    "ai",
    "rag",
    "extraction",
    "tracking",
    "email",
    "auth",
    "api",
    "storage",
    "eve_care_chat",
    "cross_check",
    "activities",
    "mail_companion",
];
